'use client';

import useSWR from 'swr';
import dayjs from 'dayjs';
import { getLocaleInfo } from '@/utils/helpers';
import { isDateWithinMonthsFromNow } from '@/utils/dates/isDateWithinMonthsFromNow';
import useLocale from './useLocale';
import { ChildInfo } from '@/components/subflow-v3/context/SubFlowV3Context';
import { QR_DATA_KEY } from '@/components/subflow-v3/components/utils';
import { isChildInfoCorrect } from '@/components/homepage-us/utils';

export interface StartingKit {
  anchor: string;
  name: string;
  description_html: string;
  age_band: string;
  price: number;
  image_uri: string;
  variant_id: string;
  customizable_options: CustomOption[];
  products: StartingKitProduct[];
  previous_play: PreviousPlay[];
  standalone_add_ons: StandaloneAddOn[];
  title: string;
  cta_text: string;
  next_page: string;
}

export interface PreviousPlay {
  anchor: string;
  name: string;
  description_html: string;
  age_band: string;
  price: number;
  image_uri: string;
  customizable_options: CustomOption[];
  variant_id: string;
}

export interface StartingKitProduct {
  anchor: string;
  name: string;
  description_html: null;
  image_uri: string;
  customizable_options: CustomOption[];
}

export interface StandaloneAddOn {
  anchor: string;
  variant_id: string;
  name: string;
  price: number;
  image_uri: string;
  description_html?: string;
}

export interface CustomOption {
  option_name: string;
  unselected_image_uri: string;
  option_prompt: string;
  option_values: CustomOptionValue[];
}

export interface CustomOptionValue {
  value: string;
  localized_label: string;
  image_uri: string;
  is_default: string;
}

export const useStartingKit = (
  childInfo: Partial<ChildInfo>,
  language: string,
) => {
  const locale = useLocale();
  const localeInfo = getLocaleInfo(locale);

  const getFormattedDob = () => {
    if (typeof window !== 'undefined') {
      const storage = JSON.parse(
        window.localStorage.getItem(QR_DATA_KEY) || '{}',
      );

      const defaultDob = `${childInfo.year}-${childInfo.month}-${childInfo.day}`;

      if (!storage || !storage.lookerInTargetQR) {
        return defaultDob;
      }

      if (
        isDateWithinMonthsFromNow({
          year: Number(childInfo.year),
          month: Number(childInfo.month),
          day: Number(childInfo.day),
          months: 3,
        })
      ) {
        return dayjs().subtract(3, 'month').format('YYYY-MM-DD');
      }

      return defaultDob;
    }

    return `${childInfo.year}-${childInfo.month}-${childInfo.day}`;
  };

  const searchParams = new URLSearchParams({
    date_of_birth: getFormattedDob(),
    child_name: childInfo.name || '',
    selected_language: language,
    language: localeInfo.language,
    currency_code: localeInfo.currencyCode.toLowerCase(),
  });

  const hasFullPaylod =
    isChildInfoCorrect(childInfo) &&
    searchParams.has('selected_language') &&
    searchParams.has('language') &&
    searchParams.has('currency_code');

  searchParams.sort();

  return useSWR<StartingKit>(
    hasFullPaylod
      ? `/api/v1/orchestration_subflow/starting_product?${searchParams}`
      : null,
    { keepPreviousData: true },
  );
};
